import React, { useState } from 'react';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isValid, parse } from 'date-fns';
import { Download, Calendar, Filter, FileText } from 'lucide-react';
import { Squad } from '../types';
import { getSessionsForPlanningReport } from '../services/reportService';
import generatePlanningReportPDF from './PlanningReportPDF';
import toast from 'react-hot-toast';

interface SessionPlanningReportProps {
  onClose: () => void;
}

type DateRange = 'week' | 'month' | 'custom';

const SessionPlanningReport: React.FC<SessionPlanningReportProps> = ({ onClose }) => {
  const [dateRange, setDateRange] = useState<DateRange>('week');
  const [startDate, setStartDate] = useState<string>(format(startOfWeek(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(endOfWeek(new Date()), 'yyyy-MM-dd'));
  const [selectedSquads, setSelectedSquads] = useState<Squad[]>(['U11', 'U13', 'U15', 'U17']);
  const [isGenerating, setIsGenerating] = useState(false);
  const [separateReports, setSeparateReports] = useState(false);

  // Handle date range change
  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    
    const today = new Date();
    
    if (range === 'week') {
      setStartDate(format(startOfWeek(today), 'yyyy-MM-dd'));
      setEndDate(format(endOfWeek(today), 'yyyy-MM-dd'));
    } else if (range === 'month') {
      setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
      setEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
    }
  };

  // Handle squad selection
  const handleSquadChange = (squad: Squad, checked: boolean) => {
    if (checked) {
      setSelectedSquads(prev => [...prev, squad]);
    } else {
      setSelectedSquads(prev => prev.filter(s => s !== squad));
    }
  };

  // Generate PDF report
  const handleGeneratePDF = async () => {
    if (!selectedSquads || selectedSquads.length === 0) {
      toast.error('Please select at least one squad');
      return;
    }

    try {
      setIsGenerating(true);
      
      const filteredSessions = await getSessionsForPlanningReport(
        startDate,
        endDate,
        selectedSquads
      );
      
      generatePlanningReportPDF({
        sessions: filteredSessions,
        startDate,
        endDate,
        selectedSquads,
        reportType: dateRange === 'month' ? 'monthly' : 'weekly',
        separateReports
      });

      toast.success('Report generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Calendar size={20} className="mr-2 text-navy-800" />
            Session Planning Report
          </h3>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-500"
            onClick={onClose}
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          <div className="space-y-6">
            <div className="form-group">
              <label className="form-label">Date Range</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <button
                  type="button"
                  className={`px-4 py-2 rounded-md ${
                    dateRange === 'week'
                      ? 'bg-navy-800 text-white'
                      : 'bg-white text-navy-800 border border-navy-800'
                  }`}
                  onClick={() => handleDateRangeChange('week')}
                >
                  This Week
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 rounded-md ${
                    dateRange === 'month'
                      ? 'bg-navy-800 text-white'
                      : 'bg-white text-navy-800 border border-navy-800'
                  }`}
                  onClick={() => handleDateRangeChange('month')}
                >
                  This Month
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 rounded-md ${
                    dateRange === 'custom'
                      ? 'bg-navy-800 text-white'
                      : 'bg-white text-navy-800 border border-navy-800'
                  }`}
                  onClick={() => setDateRange('custom')}
                >
                  Custom
                </button>
              </div>
            </div>
            
            {dateRange === 'custom' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="form-group">
                  <label htmlFor="startDate" className="form-label">Start Date</label>
                  <input
                    type="date"
                    id="startDate"
                    className="form-input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="endDate" className="form-label">End Date</label>
                  <input
                    type="date"
                    id="endDate"
                    className="form-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label flex items-center">
                <Filter size={16} className="mr-2" />
                Select Squads for Reports
              </label>
              <p className="text-sm text-gray-500 mb-2">
                Select the squads you want to generate reports for
              </p>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(['U11', 'U13', 'U15', 'U17'] as Squad[]).map(squad => (
                  <div key={squad} className="flex items-center">
                    <input
                      id={`squad-${squad}`}
                      type="checkbox"
                      checked={selectedSquads.includes(squad)}
                      onChange={(e) => handleSquadChange(squad, e.target.checked)}
                      className="h-5 w-5 text-navy-600 focus:ring-navy-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`squad-${squad}`} className="ml-2 block text-sm text-gray-900">
                      {squad}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label flex items-center">
                <FileText size={16} className="mr-2" />
                Report Format
              </label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center">
                  <input
                    id="combined-file"
                    type="radio"
                    checked={!separateReports}
                    onChange={() => setSeparateReports(false)}
                    className="h-5 w-5 text-navy-600 focus:ring-navy-500 border-gray-300"
                  />
                  <label htmlFor="combined-file" className="ml-2 block text-sm text-gray-900">
                    Generate one combined file for all squads
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="separate-files"
                    type="radio"
                    checked={separateReports}
                    onChange={() => setSeparateReports(true)}
                    className="h-5 w-5 text-navy-600 focus:ring-navy-500 border-gray-300"
                  />
                  <label htmlFor="separate-files" className="ml-2 block text-sm text-gray-900">
                    Generate separate file for each squad
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Report Preview</h4>
              <div className="text-sm text-gray-600">
                <p>Date Range: {format(new Date(startDate), 'MMM d, yyyy')} - {format(new Date(endDate), 'MMM d, yyyy')}</p>
                <p>Squads: {selectedSquads.join(', ')}</p>
                <p>Format: {separateReports ? 'Separate files per squad' : 'One combined file'}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-4 bg-gray-50 flex justify-end rounded-b-lg">
          <button
            type="button"
            className="btn-outline mr-2"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary flex items-center"
            onClick={handleGeneratePDF}
            disabled={isGenerating || selectedSquads.length === 0}
          >
            <Download size={20} className="mr-2" />
            {isGenerating ? 'Generating...' : 'Download Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionPlanningReport;